import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose, IoCar, IoSpeedometer, IoColorPalette, IoCheckmarkCircle, IoPricetag, IoCalendar } from 'react-icons/io5';
import { BsFuelPump, BsGearFill } from 'react-icons/bs';
import { FaCarSide, FaSuitcase } from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const CarDetailsModal = ({ 
  car, 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  onSeasonSelect,
  isUpdating,
  isDeleting,
  settingSeasonalPrice,
  categories,
  editingSeasonalPrice,
  onEditingSeasonalPriceChange,
  seasonalPricing
}) => {
  if (!isOpen || !car) return null;

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.3 }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl relative z-10"
          >
            {/* Header with Car Identity */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl p-6 relative">
              <button
                onClick={onClose}
                className="absolute right-4 top-4 p-2 rounded-full bg-blue-800/50 hover:bg-blue-700/50 text-white transition-colors"
              >
                <IoClose size={24} />
              </button>
              
              <div className="flex items-center space-x-4 text-white">
                <IoCar className="text-3xl" />
                <div>
                  <h2 className="text-2xl font-bold">{car.make} {car.model}</h2>
                  <p className="text-blue-200">{car.year} • {car.category}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const features = formData.get('features').split('\n').filter(f => f.trim());
                onSave({
                  make: formData.get('make'),
                  model: formData.get('model'),
                  year: Number(formData.get('year')),
                  category: formData.get('category'),
                  transmission: formData.get('transmission'),
                  fuelType: formData.get('fuelType'),
                  seats: Number(formData.get('seats')),
                  doors: Number(formData.get('doors')),
                  suitcases: Number(formData.get('suitcases')),
                  basePrice: Number(formData.get('basePrice')),
                  totalUnits: Number(formData.get('totalUnits')),
                  status: formData.get('status'),
                  description: formData.get('description'),
                  exteriorColor: formData.get('exteriorColor'),
                  interiorColor: formData.get('interiorColor'),
                  drivetrain: formData.get('drivetrain'),
                  airConditioner: formData.get('airConditioner') === 'true',
                  features,
                  specifications: {
                    engine: formData.get('engine'),
                    power: formData.get('power'),
                    consumption: formData.get('consumption')
                  }
                });
              }} className="space-y-6">
                {/* Quick Stats */}
                <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center space-x-3">
                    <BsGearFill className="text-blue-900 text-xl" />
                    <div>
                      <p className="text-sm text-gray-500">Transmission</p>
                      <select
                        name="transmission"
                        defaultValue={car.transmission}
                        required
                        className="font-medium bg-transparent border-none p-0 focus:ring-0"
                      >
                        <option value="manual">Manual</option>
                        <option value="automatic">Automatic</option>
                      </select>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center space-x-3">
                    <BsFuelPump className="text-blue-900 text-xl" />
                    <div>
                      <p className="text-sm text-gray-500">Fuel Type</p>
                      <select
                        name="fuelType"
                        defaultValue={car.fuelType}
                        required
                        className="font-medium bg-transparent border-none p-0 focus:ring-0"
                      >
                        <option value="gasoline">Gasoline</option>
                        <option value="diesel">Diesel</option>
                        <option value="electric">Electric</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center space-x-3">
                    <FaCarSide className="text-blue-900 text-xl" />
                    <div>
                      <p className="text-sm text-gray-500">Seats</p>
                      <input
                        type="number"
                        name="seats"
                        defaultValue={car.seats}
                        required
                        min="1"
                        max="15"
                        className="font-medium bg-transparent border-none p-0 w-20 focus:ring-0"
                      />
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center space-x-3">
                    <FaSuitcase className="text-blue-900 text-xl" />
                    <div>
                      <p className="text-sm text-gray-500">Luggage</p>
                      <input
                        type="number"
                        name="suitcases"
                        defaultValue={car.suitcases}
                        required
                        min="0"
                        className="font-medium bg-transparent border-none p-0 w-20 focus:ring-0"
                      />
                    </div>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Specifications */}
                  <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                    <div className="flex items-center space-x-2 mb-4">
                      <IoSpeedometer className="text-blue-900 text-xl" />
                      <h3 className="text-lg font-medium text-gray-900">Specifications</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Make</label>
                        <input
                          type="text"
                          name="make"
                          defaultValue={car.make}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Model</label>
                        <input
                          type="text"
                          name="model"
                          defaultValue={car.model}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Year</label>
                        <input
                          type="number"
                          name="year"
                          defaultValue={car.year}
                          required
                          min="1900"
                          max="2025"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <select
                          name="category"
                          defaultValue={car.category}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Drivetrain</label>
                        <select
                          name="drivetrain"
                          defaultValue={car.drivetrain}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="2WD">2WD</option>
                          <option value="4WD">4WD</option>
                          <option value="AWD">AWD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Air Conditioner</label>
                        <select
                          name="airConditioner"
                          defaultValue={car.airConditioner?.toString()}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Doors *</label>
                        <input
                          type="number"
                          name="doors"
                          defaultValue={car.doors}
                          required
                          min="1"
                          max="5"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </motion.section>

                  {/* Colors & Features */}
                  <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                    <div className="flex items-center space-x-2 mb-4">
                      <IoColorPalette className="text-blue-900 text-xl" />
                      <h3 className="text-lg font-medium text-gray-900">Colors & Features</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Exterior Color</label>
                        <input
                          type="text"
                          name="exteriorColor"
                          defaultValue={car.exteriorColor}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Interior Color</label>
                        <input
                          type="text"
                          name="interiorColor"
                          defaultValue={car.interiorColor}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Features (one per line)</label>
                        <textarea
                          name="features"
                          defaultValue={car.features?.join('\n') || ''}
                          rows="4"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        ></textarea>
                      </div>
                    </div>
                  </motion.section>
                </div>

                {/* Engine Specifications */}
                <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                  <div className="flex items-center space-x-2 mb-4">
                    <IoSpeedometer className="text-blue-900 text-xl" />
                    <h3 className="text-lg font-medium text-gray-900">Engine Specifications</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Engine</label>
                      <input
                        type="text"
                        name="engine"
                        defaultValue={car.specifications?.engine}
                        placeholder="e.g. 2.0L 4-cylinder"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Power</label>
                      <input
                        type="text"
                        name="power"
                        defaultValue={car.specifications?.power}
                        placeholder="e.g. 150 HP"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fuel Consumption</label>
                      <input
                        type="text"
                        name="consumption"
                        defaultValue={car.specifications?.consumption}
                        placeholder="e.g. 7.5L/100km"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </motion.section>

                {/* Pricing & Availability */}
                <motion.section {...fadeInUp} className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg p-6 text-white">
                  <div className="flex items-center space-x-2 mb-4">
                    <IoPricetag className="text-white text-xl" />
                    <h3 className="text-lg font-medium">Pricing & Availability</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-green-100">Base Price (€/day)</label>
                      <input
                        type="number"
                        name="basePrice"
                        defaultValue={car.basePrice}
                        required
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full rounded-md border-0 bg-green-500/50 text-white placeholder-green-200 focus:ring-2 focus:ring-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-100">Total Units</label>
                      <input
                        type="number"
                        name="totalUnits"
                        defaultValue={car.totalUnits}
                        required
                        min="0"
                        className="mt-1 block w-full rounded-md border-0 bg-green-500/50 text-white placeholder-green-200 focus:ring-2 focus:ring-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-green-100">Status</label>
                      <select
                        name="status"
                        defaultValue={car.status}
                        className="mt-1 block w-full rounded-md border-0 bg-green-500/50 text-white focus:ring-2 focus:ring-white"
                      >
                        <option value="available">Available</option>
                        <option value="maintenance">Under Maintenance</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                    </div>
                  </div>
                </motion.section>

                {/* Seasonal Pricing */}
                <motion.section {...fadeInUp} className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
                  <div className="flex items-center space-x-2 mb-4">
                    <IoCalendar className="text-blue-900 text-xl" />
                    <h3 className="text-lg font-medium text-gray-900">Seasonal Pricing</h3>
                  </div>
                  
                  {/* Predefined Seasons */}
                  <div className="space-y-4 mb-6">
                    <label className="block text-sm font-medium text-gray-700">Quick Season Setup</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        type="button"
                        onClick={() => onSeasonSelect(car._id, car.basePrice, "low")}
                        className="px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={settingSeasonalPrice?.carId === car._id}
                      >
                        {settingSeasonalPrice?.carId === car._id && settingSeasonalPrice?.season === "low" ? (
                          <div className="flex items-center justify-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            <span>Setting Low Season...</span>
                          </div>
                        ) : (
                          "Set Low Season"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => onSeasonSelect(car._id, car.basePrice, "medium")}
                        className="px-4 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={settingSeasonalPrice?.carId === car._id}
                      >
                        {settingSeasonalPrice?.carId === car._id && settingSeasonalPrice?.season === "medium" ? (
                          <div className="flex items-center justify-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            <span>Setting Medium Season...</span>
                          </div>
                        ) : (
                          "Set Medium Season"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => onSeasonSelect(car._id, car.basePrice, "high")}
                        className="px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={settingSeasonalPrice?.carId === car._id}
                      >
                        {settingSeasonalPrice?.carId === car._id && settingSeasonalPrice?.season === "high" ? (
                          <div className="flex items-center justify-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            <span>Setting High Season...</span>
                          </div>
                        ) : (
                          "Set High Season"
                        )}
                      </button>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1 mt-2">
                      <p>Low Season: Apr 1 - Jun 25, Sep 16 - Oct 30</p>
                      <p>Medium Season: Jun 26 - Jul 10, Aug 26 - Sep 15</p>
                      <p>High Season: Jul 11 - Aug 25</p>
                    </div>
                  </div>

                  {/* Custom Date Price */}
                  <div className="border-t pt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-4">Set Custom Date Price</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <DatePicker
                          selected={editingSeasonalPrice?.date}
                          onChange={(date) => onEditingSeasonalPriceChange({ ...editingSeasonalPrice, date })}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholderText="Select date"
                          minDate={new Date()}
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          value={editingSeasonalPrice?.price || ''}
                          onChange={(e) => onEditingSeasonalPriceChange({ ...editingSeasonalPrice, price: Number(e.target.value) })}
                          placeholder="Price per day"
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Current Seasonal Prices */}
                  {seasonalPricing[car._id]?.length > 0 && (
                    <div className="border-t pt-6 mt-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">Current Seasonal Prices</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {seasonalPricing[car._id]
                          .sort((a, b) => new Date(a.date) - new Date(b.date))
                          .reduce((groups, pricing) => {
                            const existingGroup = groups.find(g => g.price === pricing.dynamicPrice);
                            if (existingGroup) {
                              existingGroup.dates.push(new Date(pricing.date));
                            } else {
                              groups.push({ price: pricing.dynamicPrice, dates: [new Date(pricing.date)] });
                            }
                            return groups;
                          }, [])
                          .map((group, idx) => (
                            <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <div className="text-sm font-medium text-gray-900">€{group.price}/day</div>
                                <div className="text-xs text-gray-500">
                                  {group.dates.length} day(s)
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {group.dates.map((date, i) => (
                                  <div key={i} className="text-xs bg-white px-2 py-1 rounded border">
                                    {date.toLocaleDateString('el-GR', {day: '2-digit', month: '2-digit'})}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </motion.section>

                {/* Actions */}
                <motion.div {...fadeInUp} className="sticky bottom-0 bg-white pt-4 pb-4 border-t mt-8">
                  <div className="flex justify-between gap-4">
                    <button
                      type="button"
                      onClick={() => onDelete(car._id)}
                      disabled={isDeleting}
                      className="px-6 py-2 text-white bg-red-600 hover:bg-red-700 rounded-full shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                    >
                      {isDeleting ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        'Delete Car'
                      )}
                    </button>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdating}
                        className="px-6 py-2 text-white bg-blue-900 hover:bg-blue-800 rounded-full shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                      >
                        {isUpdating ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default CarDetailsModal;